import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { apiConfig } from "../../config";
import { IGetProductDetailsParams } from "./interfaces/api-product-details-interface";
import {
  ICrossSellProductsParams,
  IProductsGetAllParams,
  IProductsSearchParams,
} from "./interfaces/api-product-interfaces";
import ApiProducts from "./product-api";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";

export const useProduct = () => new ApiProducts(apiConfig());
export const useGetAllProductsQuery = ({
  params,
  trackingParams,
  enabled = true,
}: {
  params: IProductsGetAllParams | any;
  trackingParams?: Record<string, string | null>;
  enabled?: boolean;
}) => {
  const product = useProduct();
  return useQuery(
    [
      ...getKey(Keys.getAllProducts),
      Object.keys(params),
      Object.values(params),
    ],
    () => product.getProducts({ ...params, ...trackingParams }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      enabled: enabled,
    },
  );
};
export const useGetAllProductsInfiniteQuery = ({
  params,
  trackingParams,
  enabled = true,
}: {
  params: IProductsGetAllParams | any;
  trackingParams?: Record<string, string | null>;
  enabled?: boolean;
}) => {
  const product = useProduct();
  return useInfiniteQuery(
    [
      ...getKey(Keys.getAllProducts),
      Object.keys(params),
      Object.values(params),
      "InfiniteQuery",
    ],
    ({ pageParam = { ...params, page: 1 } }) =>
      product.getProducts({ ...pageParam, ...trackingParams }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.result.meta.current_page + 1;
        const totalPages = lastPage.result.meta.last_page;
        return nextPage <= totalPages ? { ...params, page: nextPage } : null;
      },
      enabled: enabled,
    },
  );
};
export const useGetProductDetailsQuery = ({
  params,
}: {
  params: IGetProductDetailsParams;
}) => {
  const product = useProduct();
  return useQuery(
    [...getKey(Keys.getProductDetails), Object.values(params)],
    async () => {
      const data = await product.getProductDetails({ ...params });
      if (
        data.result?.should_redirect &&
        data.result.slug &&
        data.result.slug !== params.id
      ) {
        return product.getProductDetails({ ...params, id: data.result.slug });
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      enabled: !!params.id,
    },
  );
};
export const useSearchProductsQuery = ({
  params,
}: {
  params: IProductsSearchParams;
}) => {
  const product = useProduct();
  return useQuery(
    [...getKey(Keys.searchProducts), , Object.values(params)],
    () => product.searchProducts({ ...params }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      enabled: !!params.q.trim(),
    },
  );
};

export const useToggleWishlistMutation = () => {
  const product = useProduct();
  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params?: {
        fbc?: string | null;
        fbp?: string | null;
        event_id?: string;
        ttp?: string | null;
        ttclid?: string | null;
        twclid?: string | null;
        fb_login_id?: string | null;
      };
    }) => product.toggleWishlist(id, params),
  });
};

/**
 * Cart cross-sell recommendations for the given product ids.
 *
 * The response is a plain array: no pagination, no category grouping.
 */
export const useGetCrossSellProductsQuery = ({
  params,
  enabled = true,
}: {
  params: ICrossSellProductsParams;
  enabled?: boolean;
}) => {
  const product = useProduct();
  return useQuery(
    [...getKey(Keys.getCrossSellProducts), params.ids],
    () => product.getCrossSellProducts(params),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
      // The basket can change while a surface is open (adding a recommended
      // product from inside the popup). Hold the current list until the new one
      // lands rather than blanking to a skeleton under the shopper's finger.
      keepPreviousData: true,
    },
  );
};
